import React, { FC, createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { InvoiceTabEnum } from 'containers/invoices/create-invoice/constants';
import { GenericError, GenericModal, Loader } from 'components';
import { useEditInvoiceV2Mutation, useLazyGetInvoicePDFPreviewQuery } from 'containers/invoices/api';
import DateUtils from 'utils/dateUtils';
import { useAppDispatch, useAppSelector } from 'hooks';

import { useNavigate, useParams } from 'react-router-dom';
import {
  selectCurrentTeam,
  selectInvoiceLimit,
  selectMerchantCustomizations,
  showNotifier
} from 'containers/app/appSlice';
import {
  selectAppliedLineItemLevelTaxesEditPage,
  selectDiscountFormDataEditPage,
  selectDiscountSettingsEditPage,
  selectInvoiceAttachmentsEditPage,
  selectInvoiceCustomerEditPage,
  selectInvoiceFinalAmountEditPage,
  selectInvoiceForEditPage,
  selectInvoiceLineItemsEditPage,
  selectInvoiceNotesEditPage,
  selectInvoiceNumber,
  selectInvoiceOptionsEditPage,
  selectInvoicePaymentModesEdit,
  selectInvoiceSubtotalEditPage,
  selectIsInvoiceFromJobEditPage,
  selectIsServiceFeeEnabledEditPage,
  selectJobNumberForInvoiceEditPage,
  selectPartialPaymentEnabledEditPage,
  selectSelectedServiceChargeFromEditSlice,
  selectTNCEditPage,
  toggleIsFromPreview,
  updateInvoiceEditedResponse
} from 'containers/invoices/edit-invoice/invoicesEditSlice';
import {
  checkIfAnyPaymentModeIsEligible,
  checkIfInvoiceTotalExceedsPaymentModeLimit,
  hasChangesToSync
} from 'containers/invoices/utils';
import { EditInvoicePayloadV2 } from 'containers/invoices/invoices.model';
import { getEditInvoiceV2Payload, getPDFPreviewPayloadEdit } from 'containers/invoices/edit-invoice/util';
import { ErrorResponse } from 'containers/app/app.model';
import { APIErrorMsg, APIErrorStatuses } from 'services/type';
import { NotifierTypes } from 'constants/notifierConstants';
import { throttle } from 'utils/commonUtils';
import { InvoicesRouteState } from 'routes/types';
import routesPath from 'routes/RoutesPath';
import { invoiceMinimumAmount } from 'constants/common';
import BillToSection from '../components/BillToSection';
import InvoiceLineItems from '../components/InvoiceLineItems';
import NotesSection from '../components/NotesSection';
import TermsSection from '../components/TermsSection';
import AttachmentsSection from '../components/AttachmentsSection';
import InvoiceTotalSection from '../components/InvoiceTotalSection';
import { StatusIndicator } from '../constants';
import TeamDetailsSection from '../components/TeamDetailsSection';
import InvoiceHeader from '../components/InvoiceHeader';
import { GetTeamProfileApiResponse } from 'containers/team-profile/teamProfile.model';
import { InvoiceErrorFieldsDefault } from '../create-invoice/types';
import { selectCustomizations } from 'containers/settings/invoiceSettingsSlice';
import { CrossIconvector } from 'assets/icons';
import InvoiceErrorsPopup from '../components/InvoiceErrorsPopup';
import NoSelfCheckoutPopup from '../components/NoSelfCheckoutPopup';
import InvoiceLimitExceededPopup from '../components/InvoiceLimitExceededPopup';
import PartialPaymentAvailablePopup from '../components/PartialPaymentAvailablePopup';
import SendUpdateModal from '../components/SendUpdateModal';
import InvalidInvoiceTotalModal from '../components/InvalidInvoiceTotalModal';
import { selectInvoiceByIdRawDataV2 } from 'containers/payments-side-panel/sidePanelSlice';

interface ICreateInvoiceContext {
  selectedInvoiceTab: InvoiceTabEnum;
  setSelectedInvoiceTab: React.Dispatch<React.SetStateAction<InvoiceTabEnum>>;
}

export const CreateInvoiceContext = createContext<ICreateInvoiceContext>({
  selectedInvoiceTab: InvoiceTabEnum.BILL_TO,
  setSelectedInvoiceTab: null
});

enum PageState {
  BUYER_VIEW = 'BUYER_VIEW',
  PDF_VIEW = 'PDF_VIEW'
}

interface BuyersPreviewEditProps {
  goBack: () => void;
  getTeamProfileData: GetTeamProfileApiResponse;
}

const BuyersPreviewEdit: FC<BuyersPreviewEditProps> = ({ goBack, getTeamProfileData }) => {
  const [pageState, setPageState] = useState<PageState>(PageState.BUYER_VIEW);

  const [selectedInvoiceTab, setSelectedInvoiceTab] = useState(InvoiceTabEnum.BILL_TO);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [getInvoicePDFPreview, getInvoicePDFPreviewQueryState] = useLazyGetInvoicePDFPreviewQuery();
  const {
    data: getInvoicePDFPreviewResponse,
    isSuccess: getPreviewSuccess,
    isFetching: isPDFPreviewFetching,
    isError: isPDFPreviewError
  } = getInvoicePDFPreviewQueryState;

  const selectedServiceCharge = useAppSelector(selectSelectedServiceChargeFromEditSlice);
  const selectedCustomerForInvoice = useAppSelector(selectInvoiceCustomerEditPage);
  const selectedLineItemsForInvoice = useAppSelector(selectInvoiceLineItemsEditPage);
  const selectDiscountSettingsForInvoice = useAppSelector(selectDiscountSettingsEditPage);
  const selectDiscountFormDataForInvoice = useAppSelector(selectDiscountFormDataEditPage);
  const selectInvoiceAttachmentsForInvoice = useAppSelector(selectInvoiceAttachmentsEditPage);
  const selectInvoiceNotesForInvoice = useAppSelector(selectInvoiceNotesEditPage);
  const isServiceFeeEnabled = useAppSelector(selectIsServiceFeeEnabledEditPage);
  const selectedAppliedLineItemLevelTaxes = useAppSelector(selectAppliedLineItemLevelTaxesEditPage);
  const selectedInvoiceOptionsEditPage = useAppSelector(selectInvoiceOptionsEditPage);
  const invoiceNumber = useAppSelector(selectInvoiceNumber);
  const isInvoiceFromJob = useAppSelector(selectIsInvoiceFromJobEditPage);
  const invoiceFromJobNumber = useAppSelector(selectJobNumberForInvoiceEditPage);
  const merchantCustomisations = useAppSelector(selectMerchantCustomizations);

  const selectInvoiceSubtotalForInvoice = useAppSelector(selectInvoiceSubtotalEditPage);
  const selectInvoiceFinalAmountForInvoice = useAppSelector(selectInvoiceFinalAmountEditPage);
  const selectedInvoice = useAppSelector(selectInvoiceForEditPage);
  const invoiceRawDataV2 = useAppSelector(selectInvoiceByIdRawDataV2);
  const selectedInvoiceTNC = useAppSelector(selectTNCEditPage);
  const customizations = useAppSelector(selectCustomizations);
  const partialPaymentEnabled = useAppSelector(selectPartialPaymentEnabledEditPage);
  const invoicePaymentModes = useAppSelector(selectInvoicePaymentModesEdit);
  const invoiceLimits = useAppSelector(selectInvoiceLimit);

  const invoiceRawResponse = useMemo(() => invoiceRawDataV2?.invoice, [invoiceRawDataV2]);

  const hasCustomerEmail = useMemo(
    () => selectedCustomerForInvoice?.email?.trim() !== '',
    [selectedCustomerForInvoice?.email]
  );

  const netPaidAmount = useMemo(() => {
    const totalPaidAmount = invoiceRawResponse?.totalPaidAmount || 0;
    const tipAmount = invoiceRawResponse?.tipAmount || 0;
    return totalPaidAmount - tipAmount;
  }, [invoiceRawResponse?.totalPaidAmount, invoiceRawResponse?.tipAmount]);
  const [showSyncConfirmationPopup, setShowSyncConfirmationPopup] = useState(false);
  const [showNoSelfCheckoutPopup, setShowNoSelfCheckoutPopup] = useState(false);
  const [showInvoiceErrorsPopup, setShowInvoiceErrorsPopup] = useState({
    open: false,
    errors: InvoiceErrorFieldsDefault
  });
  const [showPartialPaymentAvailablePopup, setShowPartialPaymentAvailablePopup] = useState(false);
  const [showInvoiceLimitExceededPopup, setShowInvoiceLimitExceededPopup] = useState(false);
  const [showSendUpdateToCustomerModal, setShowSendUpdateToCustomerModal] = useState(false);
  const [showInvalidTotalModal, setShowInvalidTotalModal] = useState(false);

  const { invoiceId } = useParams();

  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const [
    editInvoiceV2Mutation,
    {
      isSuccess: isEditInvoiceSuccess,
      data: invoiceEditedResponse,
      isError: isInvoiceEditError,
      error: invoiceEditError,
      isLoading: isEditInvoiceLoading
    }
  ] = useEditInvoiceV2Mutation();

  useEffect(() => {
    dispatch(toggleIsFromPreview(true));
  }, []);

  useEffect(() => {
    if (isEditInvoiceSuccess) {
      dispatch(updateInvoiceEditedResponse(invoiceEditedResponse));
      const invoiceRouteState: InvoicesRouteState = {
        showShareModal: true,
        isEditInvoiceShare: true,
        showSidePanel: false
      };
      navigate(routesPath.INVOICES, { replace: true, state: invoiceRouteState });
    }
  }, [isEditInvoiceSuccess]);

  useEffect(() => {
    if (isInvoiceEditError) {
      const error = invoiceEditError as ErrorResponse;

      if (error.status === APIErrorStatuses.STATUS_422) {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: error.data.message,
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.ERROR,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
      } else if (error.status === APIErrorStatuses.STATUS_429) {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: APIErrorMsg.LIMIT_EXCEEDED,
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.ERROR,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
      } else {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: 'Failed to update invoice',
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.ERROR,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
      }
    }
  }, [isInvoiceEditError]);

  const handleEditInvoice = (sendEmailToCustomer = false) => {
    const v2Payload = getEditInvoiceV2Payload(
      selectedInvoice,
      currentTeam,
      { enabled: selectedInvoiceTNC?.enabled || false, tncId: selectedInvoiceTNC?.tncId || 0 },
      merchantCustomisations,
      sendEmailToCustomer
    );

    const editInvoicePayloadV2: EditInvoicePayloadV2 = {
      invoiceId: invoiceId as string,
      invoicePayload: v2Payload
    };

    editInvoiceV2Mutation({ teamId: currentTeam.id.toString(), payload: editInvoicePayloadV2 });
  };

  const onEditAndShare = () => {
    let isPageIncomplete = false;
    let errors = InvoiceErrorFieldsDefault;
    if (selectedInvoice.invoiceLineItemsEditPage.length === 0) {
      isPageIncomplete = true;
      errors = { ...errors, totalAmount: true };
    }

    if (selectedInvoice.invoiceCustomerEditPage === null) {
      isPageIncomplete = true;
      errors = { ...errors, customer: true };
    }
    if (+selectedInvoice.invoiceSubTotalEditPage < (+merchantCustomisations?.minimumAmount || +invoiceMinimumAmount)) {
      isPageIncomplete = true;
      errors = { ...errors, totalAmount: true };
    }

    // Validate if invoice total is less than amount already paid
    if (netPaidAmount > 0 && selectedInvoice.finalAmountEditPage < netPaidAmount) {
      setShowInvalidTotalModal(true);
      return;
    }

    const isSelfCheckoutEmpty =
      selectedInvoice.invoicePaymentModesEdit.filter(item => item.isSelected && item.teamLevelVisiblity).length === 0;
    if (isSelfCheckoutEmpty) {
      const isAnyPaymentModeEligible = checkIfAnyPaymentModeIsEligible(
        customizations.paymentModes,
        selectedInvoice.finalAmountEditPage,
        partialPaymentEnabled
      );
      if (!isAnyPaymentModeEligible) {
        isPageIncomplete = true;
        errors = { ...errors, paymentMethods: true };
      }
    }

    if (isPageIncomplete) {
      setShowInvoiceErrorsPopup({ open: true, errors });
      return;
    }

    if (isSelfCheckoutEmpty) {
      setShowNoSelfCheckoutPopup(true);
      return;
    }

    const invoiceTotal = +selectedInvoice.finalAmountEditPage;

    // Check if partial payment is enabled but invoice total exceeds invoice limit
    if (partialPaymentEnabled && invoiceTotal > 0 && invoiceTotal > +invoiceLimits.invoiceMaxLimit) {
      setShowInvoiceLimitExceededPopup(true);
      return;
    }

    if (partialPaymentEnabled && invoiceTotal > 0) {
      const exceedsLimit = checkIfInvoiceTotalExceedsPaymentModeLimit(invoiceTotal, invoicePaymentModes);
      if (exceedsLimit) {
        setShowPartialPaymentAvailablePopup(true);
        return;
      }
    }

    if (
      isInvoiceFromJob &&
      hasChangesToSync(
        selectedInvoice.invoiceCustomerEditPage,
        selectedInvoice.initialInvoiceCustomer,
        selectedInvoice.invoiceLineItemsEditPage,
        selectedInvoice.initialInvoiceLineItemsEditPage,
        selectedInvoice.selectedServiceCharge,
        selectedInvoice.initialServiceCharge,
        selectedInvoice.invoiceSubTotalEditPage,
        selectedInvoice.initialSubTotalEditPage,
        selectedInvoice.isServiceFeeEnabledEditPage,
        selectedInvoice.isServiceFeeEnabledEditPageInitial,
        selectedInvoice.currentSurchargeEditPage,
        selectedInvoice.initialSurchargeStateEditPage
      )
    ) {
      setShowSyncConfirmationPopup(true);
    } else if (hasCustomerEmail) {
      setShowSendUpdateToCustomerModal(true);
    } else {
      handleEditInvoice();
    }
  };

  const handleOnCreateInvoiceWithoutSelfCheckout = () => {
    if (
      isInvoiceFromJob &&
      hasChangesToSync(
        selectedInvoice.invoiceCustomerEditPage,
        selectedInvoice.initialInvoiceCustomer,
        selectedInvoice.invoiceLineItemsEditPage,
        selectedInvoice.initialInvoiceLineItemsEditPage,
        selectedInvoice.selectedServiceCharge,
        selectedInvoice.initialServiceCharge,
        selectedInvoice.invoiceSubTotalEditPage,
        selectedInvoice.initialSubTotalEditPage,
        selectedInvoice.isServiceFeeEnabledEditPage,
        selectedInvoice.isServiceFeeEnabledEditPageInitial,
        selectedInvoice.currentSurchargeEditPage,
        selectedInvoice.initialSurchargeStateEditPage
      )
    ) {
      setShowSyncConfirmationPopup(true);
    } else if (hasCustomerEmail) {
      setShowSendUpdateToCustomerModal(true);
    } else {
      handleEditInvoice();
    }
  };

  const handleOnEditPaymentMethods = () => {
    handleCloseNoSelfCheckoutPopup();
    goBack();
  };

  const handleCloseNoSelfCheckoutPopup = () => {
    setShowNoSelfCheckoutPopup(false);
  };

  const handleCloseInvoiceErrorsPopup = () => {
    setShowInvoiceErrorsPopup({ open: false, errors: InvoiceErrorFieldsDefault });
  };

  const handleClosePartialPaymentAvailablePopup = () => {
    setShowPartialPaymentAvailablePopup(false);
  };

  const handleSaveAnyway = () => {
    handleClosePartialPaymentAvailablePopup();
    if (
      isInvoiceFromJob &&
      hasChangesToSync(
        selectedInvoice.invoiceCustomerEditPage,
        selectedInvoice.initialInvoiceCustomer,
        selectedInvoice.invoiceLineItemsEditPage,
        selectedInvoice.initialInvoiceLineItemsEditPage,
        selectedInvoice.selectedServiceCharge,
        selectedInvoice.initialServiceCharge,
        selectedInvoice.invoiceSubTotalEditPage,
        selectedInvoice.initialSubTotalEditPage,
        selectedInvoice.isServiceFeeEnabledEditPage,
        selectedInvoice.isServiceFeeEnabledEditPageInitial,
        selectedInvoice.currentSurchargeEditPage,
        selectedInvoice.initialSurchargeStateEditPage
      )
    ) {
      setShowSyncConfirmationPopup(true);
    } else if (hasCustomerEmail) {
      setShowSendUpdateToCustomerModal(true);
    } else {
      handleEditInvoice();
    }
  };

  const handleEditInvoiceFromPartialPaymentPopup = () => {
    handleClosePartialPaymentAvailablePopup();
    goBack();
  };

  const handleCloseInvoiceLimitExceededPopup = () => {
    setShowInvoiceLimitExceededPopup(false);
  };

  const handleEditInvoiceFromLimitExceededPopup = () => {
    handleCloseInvoiceLimitExceededPopup();
    goBack();
  };

  const handleUpdateToCustomerAndSave = (sendUpdateToCustomer = false) => {
    setShowSendUpdateToCustomerModal(false);
    handleEditInvoice(sendUpdateToCustomer);
  };

  const handleContinueFromSyncConfirmation = () => {
    setShowSyncConfirmationPopup(false);
    if (hasCustomerEmail) {
      setShowSendUpdateToCustomerModal(true);
    } else {
      handleEditInvoice();
    }
  };

  const editAndShareHandler = useCallback(throttle(onEditAndShare, 800), [selectedInvoice, isInvoiceFromJob]);

  useEffect(() => {
    if (getPreviewSuccess) {
      const pdf = new Blob([getInvoicePDFPreviewResponse], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdf);
      setPreviewUrl(url);
    }
  }, [getPreviewSuccess]);

  const renderDate = dateString => {
    let date = dateString;
    date = DateUtils.getDateInFormat({ date: new Date(date), dateFormat: 'dd MMM yyyy' });
    return date;
  };

  const navigate = useNavigate();

  const onPDFPreviewClick = () => {
    if (!previewUrl) {
      const invoiceEditPayload = getPDFPreviewPayloadEdit(
        selectedInvoice,
        currentTeam,
        { enabled: selectedInvoiceTNC?.enabled || false, tncId: selectedInvoiceTNC?.tncId || 0 },
        invoiceNumber
      );
      getInvoicePDFPreview({ teamId: currentTeam.id.toString(), payload: invoiceEditPayload });
    }
    setPageState(PageState.PDF_VIEW);
  };

  return (
    <CreateInvoiceContext.Provider value={{ selectedInvoiceTab, setSelectedInvoiceTab }}>
      <div className="flex h-full w-full flex-col border">
        <InvoiceHeader
          title="Save & Share"
          pageState={pageState}
          setPageState={setPageState}
          onPDFPreviewClick={onPDFPreviewClick}
          editAndShareHandler={editAndShareHandler}
          goBack={goBack}
          isActionLoading={isEditInvoiceLoading}
        />

        {pageState === PageState.BUYER_VIEW && (
          <div className="flex grow gap-8 bg-borderGray px-[149px] pt-8 pb-[102px]">
            <div className="flex w-[675px] flex-col gap-[30px]">
              <BillToSection customer={selectedCustomerForInvoice} />

              <InvoiceLineItems
                items={selectedLineItemsForInvoice}
                billAmount={selectInvoiceSubtotalForInvoice}
                surChargeAmount={+selectedInvoice.currentSurchargeEditPage?.valueInCurrencyWithBps || 0}
                serviceFeeEnabled={isServiceFeeEnabled}
                serviceCharges={selectedServiceCharge}
                taxes={selectedAppliedLineItemLevelTaxes}
                discountData={selectDiscountFormDataForInvoice}
                totalAmount={selectInvoiceFinalAmountForInvoice}
                selectDiscountSettingsForInvoice={selectDiscountSettingsForInvoice}
                isEditFlow={true}
              />

              {selectInvoiceNotesForInvoice && <NotesSection notes={selectInvoiceNotesForInvoice} />}

              {selectedInvoiceTNC.tnc && <TermsSection terms={selectedInvoiceTNC.tnc} />}

              {selectInvoiceAttachmentsForInvoice && selectInvoiceAttachmentsForInvoice.length > 0 && (
                <AttachmentsSection attachments={selectInvoiceAttachmentsForInvoice} />
              )}
            </div>
            <div className="flex w-[435px] flex-col gap-[30px]">
              <InvoiceTotalSection
                pendingAmount={invoiceRawResponse?.pendingAmount?.toString()}
                totalAmount={selectInvoiceFinalAmountForInvoice.toString()}
                dueDate={renderDate(selectedInvoiceOptionsEditPage.dueDate)}
                invoiceDate={renderDate(selectedInvoiceOptionsEditPage.invoiceDate)}
                status={invoiceRawResponse?.status}
                statusIndicator={invoiceRawResponse?.statusIndicator as StatusIndicator}
                paidDateAndTime={
                  invoiceRawResponse?.paidDateTime ? invoiceRawResponse?.paidDateTime : invoiceRawResponse?.paidDate
                }
              />
              <TeamDetailsSection
                teamName={currentTeam.name}
                teamPhone={getTeamProfileData.phone}
                teamEmail={getTeamProfileData.email}
                teamBillingAddress={getTeamProfileData.address}
                teamWebsite={getTeamProfileData.website}
              />
            </div>
          </div>
        )}

        {pageState === PageState.PDF_VIEW && (
          <>
            {previewUrl && (
              <div>
                <object
                  style={{
                    height: window.innerHeight,
                    width: window.innerWidth
                  }}
                  data={`${previewUrl}#toolbar=0`}
                  type="application/pdf"></object>
              </div>
            )}
            {isPDFPreviewFetching && <Loader />}
            {isPDFPreviewError && <GenericError handleErrorBack={() => setPageState(PageState.BUYER_VIEW)} />}
          </>
        )}
      </div>
      {showSyncConfirmationPopup && (
        <GenericModal
          showModal={true}
          title="Save and sync changes to the related job?"
          titleStyle="text-center"
          subTitleStyle="text-center text-primaryText text-sbase font-normal"
          subTitle={`Changes in your invoices will be applied to the associated job ${invoiceFromJobNumber}.`}
          primaryBtnLabel="Yes, continue"
          primaryBtnId="sync-job-invoice"
          primaryBtnStyle="mt-2"
          onPrimaryBtnClick={handleContinueFromSyncConfirmation}
          secondaryBtnId="cancel-sync-job-invoice"
          secondaryBtnStyle="border-none shadow-none hover:bg-transparent"
          secondaryBtnLabel="Cancel"
          onSecondaryBtnClick={() => setShowSyncConfirmationPopup(false)}
          closeIconStyle="top-3 right-3"
          CloseIcon={() => <CrossIconvector height={18} width={18} title="sync-popup-close" />}
          isActionInProgress={isEditInvoiceLoading}
          height="h-[262px]"
          className="!px-5 !py-6"
        />
      )}
      {showNoSelfCheckoutPopup && (
        <NoSelfCheckoutPopup
          isEdit={true}
          onContinue={handleOnCreateInvoiceWithoutSelfCheckout}
          onEditPaymentMethods={handleOnEditPaymentMethods}
          onClose={handleCloseNoSelfCheckoutPopup}
          isActionLoading={isEditInvoiceLoading}
        />
      )}
      {showInvoiceErrorsPopup.open && (
        <InvoiceErrorsPopup
          onClose={handleCloseInvoiceErrorsPopup}
          errorFields={showInvoiceErrorsPopup.errors}
          maximumAmount={
            partialPaymentEnabled ? +invoiceLimits.invoiceMaxLimit : +merchantCustomisations?.maximumAmount || 0
          }
          minimumAmount={
            partialPaymentEnabled ? +invoiceLimits.invoiceMinLimit : +merchantCustomisations?.minimumAmount || 0
          }
          onContinue={handleCloseInvoiceErrorsPopup}
        />
      )}
      {showPartialPaymentAvailablePopup && (
        <PartialPaymentAvailablePopup
          onClose={handleClosePartialPaymentAvailablePopup}
          paymentModes={invoicePaymentModes}
          onCreateAnyway={handleSaveAnyway}
          onEditInvoice={handleEditInvoiceFromPartialPaymentPopup}
        />
      )}
      {showInvoiceLimitExceededPopup && (
        <InvoiceLimitExceededPopup
          onClose={handleCloseInvoiceLimitExceededPopup}
          invoiceLimits={invoiceLimits}
          onEditInvoice={handleEditInvoiceFromLimitExceededPopup}
        />
      )}
      {showSendUpdateToCustomerModal && (
        <SendUpdateModal
          showModal={showSendUpdateToCustomerModal}
          onYesClick={() => handleUpdateToCustomerAndSave(true)}
          onNoClick={handleUpdateToCustomerAndSave}
          onClose={() => setShowSendUpdateToCustomerModal(false)}
          isActionInProgress={isEditInvoiceLoading}
        />
      )}
      {showInvalidTotalModal && (
        <InvalidInvoiceTotalModal
          open={showInvalidTotalModal}
          onClose={() => setShowInvalidTotalModal(false)}
          totalPaid={netPaidAmount}
          minimumTotal={netPaidAmount + 0.01}
        />
      )}
    </CreateInvoiceContext.Provider>
  );
};

export default BuyersPreviewEdit;
